import { useState } from "react";

import { api } from "../api/client.js";
import { Busy, ErrorNotice, useAsync } from "../lib/ui.js";
import { copyRichText } from "../lib/clipboard.js";

/**
 * 지식 맵 (harness/KNOWLEDGE-MAP.md §6, Phase 4-D).
 *
 * 매니페스트 한 줄에서 스킬·지식·site_api·MCP 가 갈라지는 트리를 그린다. 상단에 카운터와
 * `curl` 한 줄. 데이터는 없다 — 매니페스트가 인증 없이 열리는 문서라서다. 건수는 키가
 * 있어야 보는 `/api/kb` 의 몫이고, 여기서는 **어디에 무엇을 물을 수 있는가**만 보여 준다.
 */
export function KnowledgeMap() {
  const manifest = useAsync(() => api.manifest(), []);
  const [copied, setCopied] = useState(false);

  if (manifest.loading) return <Busy />;
  if (manifest.error || !manifest.data) return <ErrorNotice error={manifest.error} />;
  const m = manifest.data;

  // 화면은 열려 있는 것만 그린다 (C83) — 계획 항목은 문서(KNOWLEDGE-MAP.md)에 있다
  const leaves = m.skills.length + m.knowledge.ready.length + m.site_api.length + m.mcp.tools.length;
  const counters = [
    { label: "스킬", value: m.skills.length, sub: `${m.skills.filter((s) => s.status === "available").length} 사용 가능` },
    { label: "지식 경로", value: m.knowledge.ready.length, sub: "실시간 GET" },
    { label: "MCP 도구", value: m.mcp.tools.length, sub: m.mcp.transport },
    { label: "최종 항목", value: leaves, sub: "깊이 3" },
  ];

  return (
    <>
      <h1>지식 맵</h1>
      <p className="page-desc">
        에이전트가 이 사이트를 어떻게 쓰는가 — 진입점 하나에서 갈라지는 구조입니다. 데이터는 키가 있어야 보입니다.
      </p>

      <div className="row" style={{ alignItems: "stretch" }}>
        {counters.map((c) => (
          <div className="card stat" key={c.label}>
            <div className="muted">{c.label}</div>
            <div className="stat-value">{c.value}</div>
            <div className="muted" style={{ fontSize: 12 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <h2>
          진입점<span className="sub">{m.name} · v{m.version}</span>
        </h2>
        <div className="row">
          <code className="mono grow" style={{ padding: "8px 10px", background: "var(--bg)", borderRadius: 8, overflowX: "auto" }}>
            {m.quickstart}
          </code>
          <button
            onClick={() => {
              void copyRichText(m.quickstart, m.quickstart).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            {copied ? "복사됨" : "복사"}
          </button>
        </div>
        <p className="muted" style={{ margin: "8px 0 0" }}>
          매니페스트: <a href="/rma/manifest.json" target="_blank" rel="noreferrer" className="mono">/rma/manifest.json</a> · 키 발급은
          설정 → API Key ({" "}
          <span className="mono">read</span> 스코프, 발급한 사람의 스코프를 물려받습니다)
        </p>
      </div>

      <div className="tree">
        <div className="tree-root">
          <span className="tree-node root">manifest.json</span>
        </div>
        <div className="tree-branches">
          <Branch title="skills" hint="할 줄 아는 것">
            {m.skills.map((s) => (
              <Leaf key={s.id} status={s.status} title={s.name} code={s.id}>
                {s.description}
                <div className="muted mono" style={{ fontSize: 12, marginTop: 2 }}>{s.via}</div>
              </Leaf>
            ))}
          </Branch>

          <Branch title="knowledge" hint="아는 것 — 실시간 GET">
            {m.knowledge.ready.map((k) => (
              <Leaf key={k.key} status="available" title={k.key} code={k.path}>
                {k.what}
              </Leaf>
            ))}
          </Branch>

          <Branch title="site_api" hint="전 데이터 조회">
            {m.site_api.map((s) => (
              <Leaf key={s.path} status={s.status} title={s.what} code={s.path}>
                스코프 <span className="mono">{s.scope}</span>
              </Leaf>
            ))}
          </Branch>

          <Branch title="mcp" hint={`${m.mcp.transport} · 읽기 전용`}>
            {m.mcp.tools.map((t) => (
              <Leaf key={t.name} status={m.mcp.status} title={t.name}>
                {t.what}
              </Leaf>
            ))}
            <div className="muted" style={{ fontSize: 12, padding: "4px 0 0 4px" }}>{m.mcp.how}</div>
          </Branch>
        </div>
      </div>

      <div className="card">
        <h2>
          문서를 안 읽어도 지켜야 할 것<span className="sub">/api/kb/rules 의 notes 와 같은 배열</span>
        </h2>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {m.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      </div>
    </>
  );
}

function Branch({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="tree-branch">
      <div className="tree-node branch">
        {title}
        <span className="muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>{hint}</span>
      </div>
      <div className="tree-leaves">{children}</div>
    </div>
  );
}

function Leaf({
  status,
  title,
  code,
  children,
}: {
  status: "available" | "partial" | "planned";
  title: string;
  code?: string;
  children: React.ReactNode;
}) {
  const label = status === "available" ? "사용 가능" : status === "partial" ? "사용 가능" : "준비 중";
  return (
    <div className={`tree-leaf ${status}`}>
      <div className="row tight" style={{ alignItems: "baseline" }}>
        <span className={`dot ${status}`} title={label} />
        <strong>{title}</strong>
        {code && <span className="mono muted" style={{ fontSize: 12 }}>{code}</span>}
        <span className={`badge leaf-${status}`}>{label}</span>
      </div>
      <div style={{ fontSize: 13, marginTop: 2 }}>{children}</div>
    </div>
  );
}
